import axios from 'axios';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { RxRule } from '../container/account/rx/rules/engine';
import { ERegionBasic } from '../types';
import { RxCylFormat } from '../container/lensWizard/main/options/utils';
import { splunk_message } from '../common/splunk_constant';
import useSplunkLogger from '../common/hooks/useSplunkLogger';

export type TyConfig = {
  scal: {
    data: {
      storeLocator: {
        defaultGeoCode: {
          lat: number;
          lng: number;
        };
      };
    };
    rules: {
      mrn: {
        format: {
          MIN_LENGTH: number;
          MAX_LENGTH: number;
        };
      };
      incompatibleRxTable: {
        positive: Array<{
          startRange: number;
          endRange: number;
          maxPD: number;
          minPD: number;
        }>;
        negative: Array<{
          startRange: number;
          endRange: number;
          maxPD: number;
          minPD: number;
        }>;
      };
      savingProtectionPlans: {
        hasProtectionOnlyPlans: boolean;
        clspRenewalWindowDays?: number;
      };
    };
    commerceTools: {
      isWebEnabled: boolean;
      isProductAvailable: boolean;
      channels: {
        id: string;
      };
    };
    limit: number;
    lensWizard: {
      extrasSourceIdsMap: {
        [key: string]: string;
      };
    };
  };
  ncal: {
    data: {
      storeLocator: {
        defaultGeoCode: {
          lat: number;
          lng: number;
        };
      };
    };
    rules: {
      mrn: {
        format: {
          MIN_LENGTH: number;
          MAX_LENGTH: number;
        };
      };
      incompatibleRxTable: {
        positive: Array<{
          startRange: number;
          endRange: number;
          maxPD: number;
          minPD: number;
        }>;
        negative: Array<{
          startRange: number;
          endRange: number;
          maxPD: number;
          minPD: number;
        }>;
      };
      savingProtectionPlans: {
        hasProtectionOnlyPlans: boolean;
        clspRenewalWindowDays?: number;
      };
    };
    commerceTools: {
      isWebEnabled: boolean;
      isProductAvailable: boolean;
      channels: {
        id: string;
      };
    };
    limit: number;
    lensWizard: {
      extrasSourceIdsMap: {
        [key: string]: string;
      };
    };
  };
  nat: {
    data: {
      storeLocator: {
        mileOptions: number[];
        defaultOption: number;
      };
    };
    rules: {
      productCategories: {
        eyeglass_frames: {
          isFavoriteEnabled: boolean;
          new: {
            numberOfDays: string | number;
          };
          priceCheck: {
            minSellPrice: number;
            maxSellPrice: number;
          };
        };
        contact_lenses: {
          isFavoriteEnabled: boolean;
          new: {
            numberOfDays: string | number;
          };
        };
      };
    };
    commerceTools: {
      categories: {
        eyeglass_frames: {
          categoryId: string;
          inStorePurchaseOnly: {
            categoryId: string;
          };
        };
        eyeglass_lenses: {
          categoryId: string;
        };
        contact_lenses: {
          basePage: {
            categoryId: string;
          };
          securePage: {
            categoryId: string;
          };
        };
        lens_treatment: {
          categoryId: string;
        };
        savings_plans: {
          categoryId: string;
          channel: {
            scal: string;
            ncal: string;
          };
        };
      };
    };
  };
  services: {
    getBaseUserDetailsSafetyNetTimeout: number;
    getExtendedUserDetailsRetryDelay: number;
    splunk_UI_level: {
      level: string;
      enabled: boolean;
    };
    contentstack: {
      apiKey: string;
      deliveryToken: string;
      environment: string;
      region: string;
      branch: string;
      keepalive?: boolean;
      fetchOptions:{
        timeout: number;
        retryLimit: number;
        retryDelayOptions: { base: number}
      };
      livePreview: {
        enabled: boolean;
        host: string;
        management_token?: string;
      };
    };
    kpvisionbff: {
      url: string;
      timeout: number;
      envlbl: string;
      operations: {
        orderHistory: {
          pageSize: number;
        };
      };
    };
    kpvisionbffmocks: {
      url: string;
      timeout: number;
      operations: {
        orderHistory: {
          pageSize: number;
        };
      };
    };
    kpdportalbff: {
      url: string;
      timeout: number;
    };
    kpvisionweb: {
      url: string;
      timeout: number;
    };
    kpctproxybff: {
      url: string;
      timeout: number;
      envlbl: string;
    };
    fitmix: {
      apiKey: string;
      lang: string;
      url: string;
      id: string;
      sku: string;
    };
    kpotc: {
      url: string;
      timeout: number;
    };
    chatbot: {
      id: string;
      src: string;
      dataAppId: string;
      dataOrgId: string;
      dataLcwVersion: string;
      dataOrgUrl: string;
    };
    ocuco: {
      appointment: {
        schedulerUrl: string;
        scriptUrl: string;
      };
    };
    makeability: Record<keyof typeof ERegionBasic, boolean>;
  };
  rules: {
    regionalSites: any;
    mappingRules: Array<{
      regionCode: string;
      path: string;
      target: string;
      default: boolean;
    }>;
    rxValidation: Record<keyof typeof ERegionBasic, RxRule[]>;
    underageThreshold: Record<keyof typeof ERegionBasic, number>;
    rxCylFormat: Record<keyof typeof ERegionBasic, keyof typeof RxCylFormat>;
    fittingHeight: Record<number, { default: number; bifocal: number }>;
    productTag: {
      new: {
        numberOfDays: number;
      };
    };
    sessionRules: {
      maxProductInSession: number;
      logutTimeInSession: number;
    };
  };
  assets: {
    frame: string;
    frametemple: string;
  };
};
interface IConfig {
  config: TyConfig | null;
  isFetching: boolean;
  error: boolean;
}
const ConfigContext = createContext<IConfig>({} as IConfig);

export function useConfig() {
  const ctx = useContext(ConfigContext);
  return ctx;
}

export default function ConfigProvider({ children }: { children: ReactNode }): JSX.Element {
  const [config, setConfig] = useState<IConfig>({
    config: null,
    isFetching: true,
    error: false,
  });
  const { splunkLogger } = useSplunkLogger(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/v1/config');
        const config: IConfig = {
          config: response.data,
          isFetching: false,
          error: false,
        };
        setConfig(config);
      } catch (err) {
        splunkLogger({
          logType: 'error',
          data: { ...splunk_message?.ERROR?.GET_CONFIGS_ERROR, error: err },
        });
        setConfig({
          config: null,
          isFetching: false,
          error: true,
        });
      }
    };
    fetchConfig();
  }, []);

  if (config.isFetching) {
    return <></>;
  }
  if (config.error) {
    return <></>;
  }
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}
